import { Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { WMSCapabilities } from 'ol/format';
import { map, takeWhile } from 'rxjs/operators';
import { BasicResponse } from '../../../../../utils/basic-response/basic-response.model';
import {
  iwIconDeselectAll,
  iwIconSelectAll,
} from '../../../../../assets/css/ewid-svg/svg-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'gk-modal-attach-wms',
  templateUrl: './modal-attach-wms.component.html',
  styleUrls: ['./modal-attach-wms.component.scss'],
  standalone: false,
})
export class ModalAttachWmsComponent implements OnDestroy {
  private isAlive = true;
  wmsUrl: string;
  optionalName: string;
  wmsName: string;
  pendingRequest = false;
  layers: { name: string; selected: boolean; title: string }[] = [];
  allSelected = true;
  deselectAllIcon = iwIconDeselectAll;
  selectAllIcon = iwIconSelectAll;
  currentIconHtml: SafeHtml;

  @Input() successCallback: (data: {
    name: string;
    url: string;
    layers: string[];
  }) => void;

  constructor(
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
  ) {
    this.updateIcon();
  }

  handleClickConnect(): void {
    const url = this.wmsUrl.trim();
    if (!url.length) {
      this.toastr.warning(
        this.translateService.instant(
          'GK.MAP.EXTERNAL_MODAL_WMS.NO_VALID_WMS_URL',
        ),
      );

      return;
    }
    this.getCapabilities(url);
  }

  getCapabilities(url: string): void {
    this.pendingRequest = true;
    const requestUrl = `${url}?REQUEST=GetCapabilities&SERVICE=WMS`;

    this.getCapabilitiesFromWms(requestUrl)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((data) => {
        this.toastr.success(
          this.translateService.instant(
            'GK.MAP.EXTERNAL_MODAL_WMS.SUCCESS_CONNECTED',
          ),
        );

        const parser = new WMSCapabilities();
        const parsed = parser.read(data);

        const layers = parsed?.Capability?.Layer?.Layer;
        this.wmsName = parsed?.Capability?.Layer?.Title;
        this.layers = [];

        if (Array.isArray(layers)) {
          this.layers = layers.map((layer) => ({
            name: layer.Name,
            title: layer.Title,
            selected: true,
          }));
        } else if (layers?.Name) {
          this.layers.push({
            name: layers.Name,
            title: layers.Title,
            selected: true,
          });
        }

        this.pendingRequest = false;
      });
  }

  getCapabilitiesFromWms(requestUrl: string): Observable<string> {
    return this.httpClient
      .post<BasicResponse>('/api/mapa/wms/getCapabilities', { url: requestUrl })
      .pipe(
        map((response) => response.Result),
        catchError((err) => {
          this.pendingRequest = false;
          this.resetForm();

          const message =
            err.error?.ResponseStatus?.Message ||
            'Wystąpił błąd przy pobieraniu danych z WMS.';
          this.toastr.error(message);

          return throwError(() => err);
        }),
      );
  }

  addWmsToLayerTree(): void {
    const selectedLayers = this.layers.filter((layer) => layer.selected);

    if (!selectedLayers.length) {
      this.toastr.warning(
        this.translateService.instant(
          'GK.MAP.EXTERNAL_MODAL_WMS.NO_LAYERS_SELECTED',
        ),
      );
      return;
    }

    const name = this.optionalName?.trim() || this.wmsName;
    const url = this.wmsUrl?.trim();

    if (!url) {
      this.toastr.error(
        this.translateService.instant('GK.MAP.EXTERNAL_MODAL_WMS.INVALID_DATA'),
      );
      return;
    }

    if (this.successCallback) {
      this.successCallback({
        name,
        url,
        layers: selectedLayers.map((layer) => layer.name),
      });
    }

    this.activeModal.close();
  }

  handleUrlChanged(): void {
    this.resetForm();
  }

  toggleAllLayersWithIcon(): void {
    this.allSelected = !this.allSelected;
    this.layers.forEach((layer) => (layer.selected = this.allSelected));
    this.updateIcon();
  }

  updateIcon(): void {
    const icon = this.allSelected ? this.deselectAllIcon : this.selectAllIcon;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${icon.viewBox}" width="16" height="16" fill="currentColor">${icon.content}</svg>`;
    this.currentIconHtml = this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  resetForm(): void {
    this.wmsName = undefined;
    this.layers = [];
  }

  closeModal(): void {
    this.activeModal.close();
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
